import type { ReactNode } from "react";
import { ErrorState } from "~/components/ui/error-state";

export interface HandledErrorStateProps {
  /** Any error — handled or not. */
  error: unknown;
  /** Headline for a failure with no registry copy. */
  fallbackTitle?: string;
  /** Hard override of the title. Rare. */
  title?: string;
  /**
   * The glyph above the headline. Defaults to the same alert mark the inline
   * variant uses; pass a softer one (a ghost, a magnifier) where the page is
   * a dead end rather than a fault.
   */
  icon?: ReactNode;
  /** Buttons, links, an invitation to sign up — whatever the way forward is. */
  children?: ReactNode;
  /** Fill the viewport. Off inside a layout that already owns the height. */
  fullHeight?: boolean;
}

/**
 * A whole page that failed, told the way the 404 page tells it.
 *
 * The third error surface, after the toast (something just happened) and
 * `HandledErrorAlert` (a region of a page is still broken). This one is for
 * when there IS no page — the workflow is gone, the share link is dead — and
 * the alert is the only thing to look at.
 *
 * It exists because using the inline alert for that read as damage: a thin
 * red-hairline bar pinned to the top-left of an otherwise blank screen, or
 * floating in the middle of one, with no way onward. The same words in the
 * shape the app already uses for "page not found" — centred, a muted glyph, a
 * heading, and an action — read as a considered state instead of a stack
 * trace that happened to have a sentence in it.
 *
 * Copy still comes from the registry keyed by `code`, and the error id still
 * rides along: the affordance is the same everywhere, only the setting changes.
 *
 * Drawn by the Ledger `ErrorState` (`components/ui/error-state.tsx`), so a
 * page that failed and a region that failed look alike.
 */
export function HandledErrorState({
  error,
  title,
  fallbackTitle,
  icon,
  children,
  fullHeight = true,
}: HandledErrorStateProps) {
  // The Ledger ErrorState in its centred shape. No status in the headline:
  // a dead end (an expired share link, a deleted workflow) is told in the
  // registry's words, and a number would add nothing a person can act on.
  return (
    <ErrorState
      error={error}
      title={title}
      fallbackTitle={fallbackTitle}
      icon={icon}
      actions={children}
      fullHeight={fullHeight}
      showStatus={false}
    />
  );
}
